import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Shell from "../../components/Shell";
import api from "../../lib/api";
import AuthImage from "../../components/AuthImage";
import { useCart } from "../../context/CartContext";
import { Download, ArrowLeft, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const { add } = useCart();

    useEffect(() => {
        api.get(`/products/${id}`).then(r => setProduct(r.data)).catch(() => setProduct(false));
    }, [id]);

    if (product === false) return (
        <Shell variant="clinic">
            <div className="max-w-3xl mx-auto p-12 text-center"><p className="text-zinc-500">Product not found.</p></div>
        </Shell>
    );
    if (!product) return <Shell variant="clinic"><div className="max-w-3xl mx-auto p-12 text-center text-zinc-500">Loading…</div></Shell>;

    return (
        <Shell variant="clinic">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <Link to="/catalogue" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black mb-8"><ArrowLeft size={14} strokeWidth={1.5} /> Catalogue</Link>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div className="bg-zinc-50 aspect-square">
                        <AuthImage path={product.image_path} alt={product.name} className="w-full h-full object-contain p-16" />
                    </div>
                    <div>
                        <p className="eyebrow mb-3">SKU {product.sku}</p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-3">{product.name}</h1>
                        <p className="font-heading text-2xl text-black mb-8">${(product.price || 0).toFixed(2)} <span className="text-sm text-zinc-400">{product.currency || "NZD"}</span></p>
                        <p className="text-zinc-700 leading-relaxed mb-10 whitespace-pre-line">{product.description || "Detailed clinical information available on request."}</p>

                        <div className="flex items-center gap-4 mb-6">
                            <div className="inline-flex items-center border border-zinc-200 rounded-sm">
                                <button data-testid="qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 py-2 text-zinc-600 hover:bg-zinc-50"><Minus size={14} strokeWidth={1.5} /></button>
                                <input data-testid="qty-input" type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))} className="w-14 text-center bg-transparent text-sm outline-none" />
                                <button data-testid="qty-plus" onClick={() => setQty(q => q + 1)} className="px-3 py-2 text-zinc-600 hover:bg-zinc-50"><Plus size={14} strokeWidth={1.5} /></button>
                            </div>
                            <button data-testid="add-cart" onClick={() => { add(product, qty); toast.success(`${product.name} added to cart`); }} className="btn-primary flex-1">Add to order</button>
                        </div>
                        <button data-testid="buy-now" onClick={() => { add(product, qty); navigate("/cart"); }} className="btn-secondary w-full">Add and review order →</button>

                        {Array.isArray(product.pdf_paths) && product.pdf_paths.length > 0 && (
                            <div className="mt-12 pt-8 border-t border-zinc-200">
                                <p className="eyebrow mb-4">Resources</p>
                                <div className="space-y-2">
                                    {product.pdf_paths.map((f) => (
                                        <a key={f.path} data-testid={`pdf-${f.path}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.path}?auth=${localStorage.getItem("derm_token") || ""}`} target="_blank" rel="noreferrer" className="flex items-center justify-between card-base px-4 py-3 hover:border-black transition-colors">
                                            <span className="text-sm">{f.title || f.filename}</span>
                                            <Download size={16} strokeWidth={1.5} className="text-zinc-500" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Shell>
    );
}
